import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { CSSProperties, Fragment, ReactNode, useMemo, useState } from 'react';
import { ExpandPresence } from '../ExpandPresence';
import { cn } from 'utils/cn';

import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from '../BasicTable';
import NestedTableSortableHeader from './NestedTableSortableHeader';
import { sortByAccessor } from './sortUtil';

// Each tier's body and header shades, as utilities. Tier 3 sits one step further down than tier 2
// so the user can tell at a glance which tier they're scrolling through, and each header one step
// below its own body. The new design draws every tier as the list's own white card instead (see `NEW_SURFACE`).
const SURFACE = {
    default: { body: 'bg-grey-50', header: 'bg-grey-100' },
    deep: { body: 'bg-grey-100', header: 'bg-grey-200' },
} as const;

// The new design's tier: the list's white card, its header on the head's subtle surface, and the list's
// zebra restarted in every tier, so a group's first member is always unshaded.
const NEW_SURFACE = {
    body: 'new:bg-paper',
    header: 'new:bg-surface-subtle',
    stripe: 'new:bg-row-stripe',
    // `new:` outranks a plain hover, so a shaded row needs its hover restated.
    hover: 'new:hover:bg-grey-200',
} as const;

export type NestedColumn<D> = {
    id: string;
    header: ReactNode;
    /** CSS width for both header and body cell. Combined with `table-layout: fixed` to make
     *  per-column widths line up vertically with the parent list table. */
    width?: string | number;
    align?: 'left' | 'right' | 'center';
    /** When provided, the header becomes click-to-sort and rows are sorted client-side
     *  by this accessor. Numeric and string values are both supported (strings use a
     *  locale-aware collator); nulls always drop to the end. ISO 8601 date strings sort
     *  correctly without extra parsing. */
    sortAccessor?: (row: D) => number | string | null | undefined;
    Cell: (row: D) => ReactNode;
};

type NestedTableProps<D> = {
    data: D[];
    columns: NestedColumn<D>[];
    getRowKey: (row: D) => string | number;
    /** Row-level expansion. When `isRowExpanded(row)` is true, the table renders an extra
     *  `colSpan`'d row beneath with `renderExpandedContent(row)`. The trailing expand-toggle
     *  column is added automatically iff `renderExpandedContent` is provided. */
    isRowExpanded?: (row: D) => boolean;
    onToggleRow?: (row: D) => void;
    renderExpandedContent?: (row: D) => ReactNode;
    /** Optional banner shown above the table body inside the bordered container — typically a
     *  truncation/info Alert (e.g. "showing 200 of 543 lines"). */
    banner?: ReactNode;
    /** Row density. `small` halves the cell padding, matching the tighter row spacing used in the
     *  innermost tier of the Expenses grouped view; default `medium` matches the next tier up. */
    size?: 'small' | 'medium';
    expandRowAriaLabel?: (row: D, isExpanded: boolean) => string;
    /** Width of the trailing expand-toggle column. Default 48px. */
    expandColumnWidth?: string;
    /** Notified whenever the local sort changes. */
    onSortChange?: (next: string) => void;
    /** Surface tone for this tier. Tier 2 (default) sits one notch below the parent table's
     *  white; deeper tiers pass `'deep'` for the slightly darker shade, so users scrolling
     *  through nested rows can tell which level they're in. */
    surface?: 'default' | 'deep';
    /** Optional accent colour rendered as a thin inset bar on the left and right edges of
     *  a row — used in My Portfolio to highlight member rows the user is RP/RM of.
     *  Returning a falsy value (undefined / null) skips the bars for that row. */
    rowEdgeColor?: (row: D) => string | null | undefined;
    /** Columns at least as wide as their content, `width` only sharing out the room left (`table-layout: auto`):
     *  for a tier whose columns need not line up with the list above it. */
    autoLayout?: boolean;
};

const DEFAULT_EXPAND_COLUMN_WIDTH = '48px';

/**
 * Generic styled table for rendering one tier of a nested list-table view (i.e. the content shown
 * when a row of the parent list table is expanded). Designed to be composable so the same component
 * renders both intermediate tiers (rows that further expand) and leaf tiers (rows that don't), and so
 * multiple call sites — Expenses grouped view, My Portfolio grouped view — share a single
 * nested-table look-and-feel.
 *
 * Column widths in `columns` plus `table-layout: fixed` keep the cells aligned vertically with
 * whatever sits above them; the parent list table already uses fixed layout when
 * `fixedLayoutWidths` is on, so matching widths line up flush.
 *
 * Sort state is local: each instance manages its own active column / direction so sorting one
 * sibling expansion doesn't reorder another. Caller can subscribe via `onSortChange`.
 */
const NestedTable = <D extends object>({
    data,
    columns,
    getRowKey,
    isRowExpanded,
    onToggleRow,
    renderExpandedContent,
    banner,
    size,
    expandRowAriaLabel,
    expandColumnWidth = DEFAULT_EXPAND_COLUMN_WIDTH,
    onSortChange,
    surface = 'default',
    rowEdgeColor,
    autoLayout = false,
}: NestedTableProps<D>) => {
    const shades = SURFACE[surface];
    const cellClass = cn(SIMPLE_TABLE_CELL_CLASS, size === 'small' && 'p-2');
    const headClass = cn(SIMPLE_TABLE_HEAD_CLASS, 'font-semibold', size === 'small' && 'p-2');

    const [sort, setSortInternal] = useState<string>('');
    const setSort = (next: string) => {
        setSortInternal(next);
        onSortChange?.(next);
    };

    // Both pieces are required to make a working expand UI: the rendered content AND a way
    // for the user to trigger it. If either is missing the column is omitted entirely so the
    // header doesn't reserve space for a non-existent toggle button.
    const showExpandColumn = !!(renderExpandedContent && onToggleRow);
    const totalColumnCount = columns.length + (showExpandColumn ? 1 : 0);

    const sortedData = useMemo(() => {
        const [sortId, sortDir] = sort.split(',');
        if (!sortId) return data;
        const column = columns.find((c) => c.id === sortId);
        if (!column?.sortAccessor) return data;
        return sortByAccessor(data, column.sortAccessor, sortDir === 'desc');
    }, [data, columns, sort]);

    return (
        // An auto table is only as wide as its content: the card spans its row itself, in a flex cell too.
        <div
            className={cn(
                'border-divider overflow-hidden rounded-sm border',
                shades.body,
                NEW_SURFACE.body,
                autoLayout && 'w-full'
            )}
        >
            {banner ? <div className="p-3 pb-0">{banner}</div> : null}
            <table className={cn('w-full text-left text-sm', autoLayout ? 'table-auto' : 'table-fixed')}>
                <TableHeader className="[&_tr]:border-b-0">
                    <TableRow className={cn(SIMPLE_TABLE_ROW_CLASS, shades.header, NEW_SURFACE.header)}>
                        {columns.map((col) => (
                            <TableHead
                                key={col.id}
                                className={cn(headClass, shades.header, NEW_SURFACE.header)}
                                style={{ width: col.width, textAlign: col.align }}
                            >
                                {col.sortAccessor ? (
                                    <NestedTableSortableHeader
                                        columnId={col.id}
                                        label={col.header}
                                        sort={sort}
                                        onSortChange={setSort}
                                        align={col.align === 'right' ? 'right' : 'left'}
                                    />
                                ) : (
                                    col.header
                                )}
                            </TableHead>
                        ))}
                        {showExpandColumn ? (
                            <TableHead
                                className={cn(headClass, shades.header, NEW_SURFACE.header)}
                                style={{ width: expandColumnWidth }}
                            />
                        ) : null}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {sortedData.map((row, rowIndex) => {
                        const isExpanded = !!isRowExpanded?.(row);
                        const edgeColor = rowEdgeColor ? rowEdgeColor(row) : null;
                        // Last visible cell index = last data column when no expand toggle, else
                        // the toggle column. Used to pick where the right-edge bar attaches.
                        const lastVisibleIndex = columns.length - (showExpandColumn ? 0 : 1);
                        const edgeStyle = (index: number): CSSProperties | undefined =>
                            edgeColor
                                ? index === 0
                                    ? { boxShadow: `inset 2px 0 0 0 ${edgeColor}` }
                                    : index === lastVisibleIndex
                                      ? { boxShadow: `inset -2px 0 0 0 ${edgeColor}` }
                                      : undefined
                                : undefined;
                        // An expanded row drops its divider so it reads as one continuous block with
                        // the nested tier rendered immediately below, mirroring the tier-1 ↔ tier-2 join.
                        const rowCellClass = cn(cellClass, isExpanded && 'border-b-0');
                        return (
                            <Fragment key={getRowKey(row)}>
                                <TableRow
                                    className={cn(
                                        SIMPLE_TABLE_ROW_CLASS,
                                        'hover:bg-grey-200',
                                        rowIndex % 2 === 1 && NEW_SURFACE.stripe,
                                        NEW_SURFACE.hover
                                    )}
                                >
                                    {columns.map((col, index) => (
                                        <TableCell
                                            key={col.id}
                                            className={cn(rowCellClass, col.align === 'right' && 'whitespace-nowrap')}
                                            style={{ width: col.width, textAlign: col.align, ...edgeStyle(index) }}
                                        >
                                            {col.Cell(row)}
                                        </TableCell>
                                    ))}
                                    {showExpandColumn ? (
                                        // Center-aligned so the chevron sits as its own column
                                        // instead of pinning against the table's right border
                                        // next to the rightmost data cell. No inline padding, so the
                                        // chevron actually centres: the cell's 16px would leave a
                                        // content box narrower than the button, which would then
                                        // overflow to one side. The `!` beats the last cell's 16px.
                                        <TableCell
                                            className={cn(rowCellClass, 'px-0! text-center')}
                                            style={{ width: expandColumnWidth, ...edgeStyle(columns.length) }}
                                        >
                                            {onToggleRow ? (
                                                <Button
                                                    variant="ghost"
                                                    // 30px and middle-aligned, so the row keeps its measured
                                                    // height: on the text baseline the button would add the
                                                    // descender space below it.
                                                    className={cn('size-7.5 p-0 align-middle', GLYPH_OWN_SIZE)}
                                                    onClick={() => onToggleRow(row)}
                                                    aria-label={
                                                        expandRowAriaLabel
                                                            ? expandRowAriaLabel(row, isExpanded)
                                                            : undefined
                                                    }
                                                >
                                                    <ExpandMoreRoundedIcon
                                                        className={cn(
                                                            'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                                            isExpanded && '-scale-y-100'
                                                        )}
                                                    />
                                                </Button>
                                            ) : null}
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                                {renderExpandedContent ? (
                                    <ExpandPresence open={isExpanded}>
                                        {(phase) => (
                                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                                <TableCell
                                                    data-expand-phase={phase}
                                                    colSpan={totalColumnCount}
                                                    className={cn(cellClass, 'py-3', shades.body)}
                                                >
                                                    {renderExpandedContent(row)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </ExpandPresence>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </table>
        </div>
    );
};

export default NestedTable;
